import { useMemo, useState } from 'react'
import { X, Network } from 'lucide-react'
import type { FitThesis } from '@ispot/shared'
import type { PersonaSummary } from './FitThesisMatrix'

interface Props {
  personas: PersonaSummary[]
  existing: FitThesis[]
  onCancel(): void
  onCreate(personaSessionId: string, jtbdId: string, painId?: string): Promise<void>
}

export function NewThesisDialog({ personas, existing, onCancel, onCreate }: Props) {
  const [personaId, setPersonaId] = useState<string>('')
  const [jtbdId, setJtbdId] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const takenKeys = useMemo(() => new Set(existing.map(t => `${t.personaSessionId}::${t.jtbdId}`)), [existing])
  const selectedPersona = personas.find(p => p.sessionId === personaId) ?? null

  const validJtbds = selectedPersona?.jtbds ?? []

  async function handleSubmit() {
    if (!personaId || !jtbdId) return
    setSubmitting(true)
    setError(null)
    try {
      await onCreate(personaId, jtbdId)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create thesis')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(42,36,30,0.42)] backdrop-blur-sm" onClick={onCancel}>
      <div
        className="w-[520px] max-w-[calc(100vw-64px)] overflow-hidden rounded-lg border border-border bg-bg-card shadow-[0_12px_40px_rgba(42,36,30,0.22)]"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-7 py-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md p-2" style={{ background: 'var(--color-accent-50)' }}>
              <Network className="h-4 w-4 text-accent-500" />
            </div>
            <div>
              <h2 className="font-display text-heading font-semibold text-fg">New fit thesis</h2>
              <p className="mt-1 text-body text-fg-secondary">Pick one persona and one JTBD. Skerpa takes it from there.</p>
            </div>
          </div>
          <button onClick={onCancel} className="rounded-full bg-bg-secondary p-1.5 text-fg-muted hover:bg-border">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-col gap-5 px-7 py-6">
          <div className="flex flex-col gap-2">
            <label className="text-body font-medium text-fg">Persona</label>
            <select
              value={personaId}
              onChange={e => { setPersonaId(e.target.value); setJtbdId('') }}
              className="rounded-md border border-border bg-bg-card px-3.5 py-2.5 text-body text-fg outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
            >
              <option value="">Select a persona...</option>
              {personas.map(p => (
                <option key={p.sessionId} value={p.sessionId}>
                  {p.name}{p.segment ? ` - ${p.segment}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-body font-medium text-fg">Job to be done</label>
            <select
              value={jtbdId}
              onChange={e => setJtbdId(e.target.value)}
              disabled={!personaId}
              className="rounded-md border border-border bg-bg-card px-3.5 py-2.5 text-body text-fg outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15 disabled:bg-bg-secondary disabled:text-fg-muted"
            >
              <option value="">{personaId ? 'Select a JTBD...' : 'Pick a persona first'}</option>
              {validJtbds.map(j => {
                const taken = takenKeys.has(`${personaId}::${j.id}`)
                return (
                  <option key={j.id} value={j.id} disabled={taken}>
                    {j.label}{taken ? ' - already has a thesis' : ''}
                  </option>
                )
              })}
            </select>
            {selectedPersona && validJtbds.length === 0 && (
              <p className="text-caption text-fg-muted">This persona has no JTBDs yet. Add one in the persona workshop.</p>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-danger-fg bg-danger-bg px-3 py-2 text-body text-danger-fg">{error}</div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-border bg-bg-secondary px-7 py-4">
          <span className="text-caption text-fg-muted">Next step: Skerpa opens the chat drawer.</span>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="rounded-md bg-bg-secondary px-4 py-2 text-body font-medium text-fg transition hover:bg-border"
            >Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={!personaId || !jtbdId || submitting}
              className="rounded-md bg-accent-500 px-4 py-2 text-body font-medium text-bg-card transition hover:bg-accent-600 disabled:opacity-40"
            >{submitting ? 'Creating...' : 'Create thesis'}</button>
          </div>
        </footer>
      </div>
    </div>
  )
}
